"use client"

import { addHours, startOfDay, isSameHour } from "date-fns"
import { cn } from "@/lib/utils"
import { Avatar, AvatarImage } from "@/components/ui/avatar"
import type { CalendarEvent } from "./calendar"
import UserSvg from "@/assets/userSvg.svg"
import { CalendarEventActions } from "./calendar-event-actions"

interface DayViewProps {
    currentDate: Date
    events: CalendarEvent[]
    onViewEvent?: (event: CalendarEvent) => void
    onEditEvent?: (event: CalendarEvent) => void
    onDeleteEvent?: (event: CalendarEvent) => void
    refetch?: () => void
    access?: string
    hideEventActions?: boolean
    onDateClick?: (date: Date) => void
}

export function DayView({ currentDate, events, onViewEvent, onEditEvent, onDeleteEvent, refetch, access, hideEventActions, onDateClick }: DayViewProps) {
    const hours = Array.from({ length: 24 }, (_, i) => i) // 0:00 to 24:00

    // Get events for a specific hour
    const getEventsForHour = (hour: number) => {
        const hourStart = addHours(startOfDay(currentDate), hour)

        return events.filter((event) => {
            const eventStart = new Date(event.start)
            const eventHour = eventStart.getHours()
            return isSameHour(eventStart, hourStart)
        })
    }

    return (
        <div className="flex-1 flex flex-col relative overflow-y-auto">
            {hours.map((hour) => {
                const eventsForHour = getEventsForHour(hour)

                return (
                    <div key={hour} className="flex border-b min-h-[100px]">
                        <div className="sticky left-0 z-10 w-16 flex-shrink-0 border-r p-2 text-xs text-gray-500 bg-white">{hour}:00</div>
                        <div
                            className="flex-1 p-1 hover:bg-gray-50/50 cursor-pointer"
                            onClick={() => {
                                const clickedDate = addHours(startOfDay(currentDate), hour);
                                onDateClick?.(clickedDate);
                            }}
                        >
                            {eventsForHour.map((event) => {
                                // Map color to static Tailwind classes
                                const getColorClasses = (color?: string) => {
                                    const colorMap: Record<string, string> = {
                                        purple: "bg-purple-200 border-purple-400 text-purple-900 hover:bg-purple-250",
                                        blue: "bg-blue-200 border-blue-400 text-blue-900 hover:bg-blue-250",
                                        green: "bg-green-200 border-green-400 text-green-900 hover:bg-green-250",
                                        red: "bg-red-200 border-red-400 text-red-900 hover:bg-red-250",
                                        yellow: "bg-yellow-200 border-yellow-400 text-yellow-900 hover:bg-yellow-250",
                                        orange: "bg-orange-200 border-orange-400 text-orange-900 hover:bg-orange-250",
                                        teal: "bg-teal-200 border-teal-400 text-teal-900 hover:bg-teal-250",
                                        pink: "bg-pink-200 border-pink-400 text-pink-900 hover:bg-pink-250",
                                        indigo: "bg-indigo-200 border-indigo-400 text-indigo-900 hover:bg-indigo-250",
                                        gray: "bg-gray-200 border-gray-400 text-gray-900 hover:bg-gray-250",
                                    };
                                    return colorMap[color || ""] || "bg-blue-200 border-blue-400 text-blue-900 hover:bg-blue-250";
                                };

                                return (
                                    <div
                                        key={event.id}
                                        className={cn(
                                            "p-2 rounded-md mb-1 border transition-colors",
                                            getColorClasses(event.color),
                                            onViewEvent && "cursor-pointer"
                                        )}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onViewEvent?.(event);
                                        }}
                                    >
                                        <div className="flex items-start justify-between gap-1 mb-1">
                                            <div
                                                className="text-sm font-medium flex-1"
                                            >
                                                {event.title}
                                            </div>
                                            {!hideEventActions && (
                                                <CalendarEventActions
                                                    event={event}
                                                    onEdit={onEditEvent}
                                                    onView={onViewEvent}
                                                    onDelete={onDeleteEvent}
                                                    refetch={refetch}
                                                    access={access}
                                                    size="sm"
                                                    variant="dropdown"
                                                />
                                            )}
                                        </div>
                                        {
                                            event.tags && (
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {event.tags.map((tag, idx) => (
                                                        <span
                                                            key={idx}
                                                            className={cn("px-1.5 py-0.5 rounded-sm text-white text-[10px]", `bg-${tag.color}-500`)}
                                                        >
                                                            {tag.label}
                                                        </span>
                                                    ))}
                                                </div>
                                            )
                                        }
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )
            })}
        </div >
    )
}

